"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Clock, Plane, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
const airportRates = [{
  route: "Downtown / Loop ↔ O'Hare (ORD)",
  type: "Airport Transfer",
  sedanPrice: "$95",
  suvPrice: "$135",
  notes: "Curbside or baggage meet & greet"
}, {
  route: "Downtown / Loop ↔ Midway (MDW)",
  type: "Airport Transfer",
  sedanPrice: "$85",
  suvPrice: "$125",
  notes: "Curbside or baggage meet & greet"
}, {
  route: "Western Suburbs ↔ O'Hare (ORD)",
  type: "Airport Transfer",
  sedanPrice: "$110",
  suvPrice: "$150",
  notes: "Flight tracking included"
}, {
  route: "Western Suburbs ↔ Midway (MDW)",
  type: "Airport Transfer",
  sedanPrice: "$105",
  suvPrice: "$145",
  notes: "Flight tracking included"
}];
const hourlyRates = [{
  service: "As-Directed Hourly Chauffeur",
  duration: "3-Hour Minimum",
  sedanPrice: "$80 / hr",
  suvPrice: "$110 / hr",
  notes: "Unlimited stops within Chicago metro"
}, {
  service: "Full-Day Executive Charter",
  duration: "8-Hour Package",
  sedanPrice: "$600 flat",
  suvPrice: "$820 flat",
  notes: "Dedicated driver on standby all day"
}, {
  service: "Interstate & Regional Trips",
  duration: "Point-to-Point",
  sedanPrice: "Custom Quote",
  suvPrice: "Custom Quote",
  notes: "IL, IN, WI, MI, and IA service"
}];
const perks = [{
  title: "No surge pricing",
  description: "Your agreed flat rate stays fixed regardless of peak hours, bad weather, or high traffic."
}, {
  title: "Complimentary wait time",
  description: "Enjoy 45 minutes of free waiting on domestic arrivals and 60 minutes for international flights."
}, {
  title: "All tolls & taxes included",
  description: "Every quote covers standard highway tolls, airport terminal fees, and local taxes upfront."
}, {
  title: "Live flight tracking",
  description: "Chauffeurs monitor your flight schedule in real time and adjust pickup times automatically."
}];
export default function Pricing() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 24
    },
    visible: (custom: number = 0) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
        delay: shouldReduceMotion ? 0 : custom * 0.08
      }
    })
  };
  return <section id="pricing" data-nav="dark" className="dark bg-background text-foreground py-20 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl">
        {/* Header */}
        <div className="max-w-3xl mb-14 md:mb-16">
          <motion.div variants={fadeUpVariant} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-accent/40 text-accent-foreground text-xs font-semibold tracking-wider uppercase mb-4">
            <Sparkles className="size-3.5 text-primary" />
            Transparent Rates
          </motion.div>

          <motion.h2 variants={fadeUpVariant} initial="hidden" whileInView="visible" custom={1} viewport={{
          once: true,
          amount: 0.2
        }} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground font-default">
            Transparent pricing with <span className="text-primary">zero hidden fees</span>
          </motion.h2>

          <motion.p variants={fadeUpVariant} initial="hidden" whileInView="visible" custom={2} viewport={{
          once: true,
          amount: 0.2
        }} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Plan your trip with guaranteed flat rates for airport transfers and hourly charters across Chicago.
          </motion.p>
        </div>

        {/* Pricing Tables Container */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-start">
          {/* Main Tables Area */}
          <div className="lg:col-span-8 space-y-10">
            {/* Table 1: Airport Transfers */}
            <motion.div variants={fadeUpVariant} initial="hidden" whileInView="visible" custom={1} viewport={{
            once: true,
            amount: 0.1
          }} className="rounded-xl border border-border bg-card p-6 md:p-8 shadow-md">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-md bg-muted text-primary">
                    <Plane className="size-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-card-foreground font-default">
                      Airport Transfer Flat Rates
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Guaranteed curbside and terminal transfers
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                  <Clock className="size-3.5 text-primary" />
                  <span>24/7 Dispatch Ready</span>
                </div>
              </div>

              <div className="mt-4 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground font-semibold py-3">Route</TableHead>
                      <TableHead className="text-muted-foreground font-semibold py-3 text-right font-mono">Executive Sedan</TableHead>
                      <TableHead className="text-muted-foreground font-semibold py-3 text-right font-mono">Premium SUV</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {airportRates.map((item, idx) => <TableRow key={item.route} data-index={idx} className="border-border/60 hover:bg-muted/40 transition-colors">
                        <TableCell className="py-4">
                          <p className="font-medium text-foreground text-sm">{item.route}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{item.notes}</p>
                        </TableCell>
                        <TableCell className="text-right py-4 font-mono font-bold text-base text-primary">
                          {item.sedanPrice}
                        </TableCell>
                        <TableCell className="text-right py-4 font-mono font-bold text-base text-foreground">
                          {item.suvPrice}
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </div>
            </motion.div>

            {/* Table 2: Hourly Charters */}
            <motion.div variants={fadeUpVariant} initial="hidden" whileInView="visible" custom={2} viewport={{
            once: true,
            amount: 0.1
          }} className="rounded-xl border border-border bg-card p-6 md:p-8 shadow-md">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-md bg-muted text-primary">
                    <Clock className="size-5" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-card-foreground font-default">
                      Hourly & Charter Minimums
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Flexible chauffeur hire for meetings, events, and roadshows
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                  <ShieldCheck className="size-3.5 text-primary" />
                  <span>Licensed & Insured</span>
                </div>
              </div>

              <div className="mt-4 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground font-semibold py-3">Service Tier</TableHead>
                      <TableHead className="text-muted-foreground font-semibold py-3 text-right font-mono">Executive Sedan</TableHead>
                      <TableHead className="text-muted-foreground font-semibold py-3 text-right font-mono">Premium SUV</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hourlyRates.map((item, idx) => <TableRow key={item.service} data-index={idx} className="border-border/60 hover:bg-muted/40 transition-colors">
                        <TableCell className="py-4">
                          <p className="font-medium text-foreground text-sm">{item.service}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{item.duration} • {item.notes}</p>
                        </TableCell>
                        <TableCell className="text-right py-4 font-mono font-bold text-base text-primary">
                          {item.sedanPrice}
                        </TableCell>
                        <TableCell className="text-right py-4 font-mono font-bold text-base text-foreground">
                          {item.suvPrice}
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </div>
            </motion.div>
          </div>

          {/* Sidebar CTA & Guarantee Box */}
          <motion.div variants={fadeUpVariant} initial="hidden" whileInView="visible" custom={3} viewport={{
          once: true,
          amount: 0.1
        }} className="lg:col-span-4 space-y-6">
            {/* Quick Booking Action Card */}
            <div className="rounded-xl border border-primary/30 bg-card p-6 md:p-8 shadow-lg relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-2xl pointer-events-none" />
              
              <h3 className="text-xl font-bold text-card-foreground font-default mb-2">
                Need a tailored quote?
              </h3>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                Book your ride online in minutes or request a customized multi-city itinerary.
              </p>

              <div className="space-y-3">
                <Button asChild size="lg" className="w-full justify-between font-bold text-base shadow-md">
                  <Link to="/OrderPage">
                    <span>Reserve Chauffeur</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full">
                  <Link to="/ContactUsPage">
                    Contact 24/7 Dispatch
                  </Link>
                </Button>
              </div>

              <div className="mt-6 pt-6 border-t border-border/80 space-y-2.5">
                <p className="text-xs text-muted-foreground flex items-center justify-between">
                  <span>Dispatch phone:</span>
                  <a href="tel:3125550100" className="text-foreground font-mono font-semibold hover:text-primary transition-colors">
                    (312) 555-0100
                  </a>
                </p>
                <p className="text-xs text-muted-foreground flex items-center justify-between">
                  <span>Service area:</span>
                  <span className="text-foreground font-medium">Chicago & Tri-State</span>
                </p>
              </div>
            </div>

            {/* Inclusions summary panel */}
            <div className="rounded-xl border border-border bg-card/60 p-6 space-y-4">
              <h4 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                All-Inclusive Standards
              </h4>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Licensed City of Chicago chauffeurs</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Complimentary bottled water and Wi-Fi</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Child safety seats available upon request</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Automated digital trip receipts and invoicing</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Value Pillars / Policy Notes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {perks.map((perk, i) => <motion.div key={perk.title} data-index={i} variants={fadeUpVariant} initial="hidden" whileInView="visible" custom={i + 1} viewport={{
          once: true,
          amount: 0.1
        }} className="rounded-lg border border-border/80 bg-card/40 p-5 flex flex-col justify-between hover:border-primary/40 transition-colors">
              <div>
                <div className="size-2 rounded-full bg-primary mb-3" />
                <h4 className="text-base font-bold text-card-foreground font-default mb-1.5">
                  {perk.title}
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {perk.description}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}